"use client";

// Library
import { useTranslations } from "next-intl";
import { useRef, useState } from "react";
import { toast } from "react-toastify";
import {
    GoogleReCaptchaProvider,
    useGoogleReCaptcha,
} from "react-google-recaptcha-v3";
import ReCAPTCHA from "react-google-recaptcha";

// Components
import Button from "@/components/ui/Button/Button";

const EMPTY_FORM = { name: "", email: "", message: "" };

const FIELD_CLASS =
    "w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-box-outline dark:bg-background dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent transition-all outline-hidden";
const LABEL_CLASS =
    "block text-sm font-medium dark:text-white text-gray-700 mb-2";

function SupportFormInner() {
    const t = useTranslations();
    const { executeRecaptcha } = useGoogleReCaptcha();
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState(EMPTY_FORM);

    // Repli reCAPTCHA v2 quand le score v3 est jugé trop bas côté serveur.
    const [showRecaptchaV2, setShowRecaptchaV2] = useState(false);
    const [recaptchaV2Token, setRecaptchaV2Token] = useState(null);
    const recaptchaV2Ref = useRef(null);

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value,
        });
    };

    const submitWithRecaptcha = async (
        data,
        recaptchaToken,
        recaptchaVersion,
    ) => {
        const response = await fetch("/api/support/contact", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                ...data,
                recaptchaToken,
                recaptchaVersion,
            }),
        });

        const responseData = await response.json();

        if (response.ok) {
            return { success: true };
        }

        return {
            success: false,
            error: responseData.error,
            message: responseData.message,
        };
    };

    const resetForm = () => {
        setFormData(EMPTY_FORM);
        setShowRecaptchaV2(false);
        setRecaptchaV2Token(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            // Script reCAPTCHA indisponible (bloqueur, réseau) : « réessayer » ne
            // mènerait nulle part, on renvoie vers l'adresse e-mail directe.
            if (!executeRecaptcha) {
                toast.error(t("support_form_error"));
                setLoading(false);
                return;
            }

            const recaptchaToken = await executeRecaptcha("support_contact");
            const result = await submitWithRecaptcha(
                formData,
                recaptchaToken,
                "v3",
            );

            if (result.success) {
                toast.success(t("support_form_success"));
                resetForm();
            } else if (result.error === "RECAPTCHA_LOW_SCORE") {
                setShowRecaptchaV2(true);
                toast.info(t("signup_recaptcha_verify"));
            } else {
                // Les messages de la route sont en français en dur : ils servent
                // au debug, jamais à l'affichage (cette page sert aussi /en/support).
                console.error("Support form error:", result.error, result.message);
                toast.error(t("support_form_error"));
            }
        } catch (error) {
            console.error("Error submitting support form:", error);
            toast.error(t("support_form_error"));
        } finally {
            setLoading(false);
        }
    };

    // Button ne rend pas d'attribut type : sans preventDefault, ce bouton
    // soumettrait le formulaire et rejouerait la v3 qu'on vient d'échouer.
    const handleRecaptchaV2Submit = async (e) => {
        e?.preventDefault();

        if (!recaptchaV2Token) return;

        // Ce bouton n'est pas un submit : la validation HTML5 required ne joue pas.
        if (!formData.name || !formData.email || !formData.message) {
            toast.error(t("support_form_error"));
            return;
        }

        setLoading(true);

        try {
            const result = await submitWithRecaptcha(
                formData,
                recaptchaV2Token,
                "v2",
            );

            if (result.success) {
                toast.success(t("support_form_success"));
                resetForm();
            } else {
                console.error("Support form error:", result.error, result.message);
                toast.error(t("support_form_error"));
                recaptchaV2Ref.current?.reset();
                setRecaptchaV2Token(null);
            }
        } catch (error) {
            console.error("Error submitting support form:", error);
            toast.error(t("support_form_error"));
        } finally {
            setLoading(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-6">
            <div>
                <label htmlFor="name" className={LABEL_CLASS}>
                    {t("support_form_name")}
                    <span className="text-red-500 ml-1">*</span>
                </label>
                <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    maxLength={120}
                    autoComplete="name"
                    className={FIELD_CLASS}
                    placeholder={t("support_form_name_placeholder")}
                />
            </div>

            <div>
                <label htmlFor="email" className={LABEL_CLASS}>
                    {t("support_form_email")}
                    <span className="text-red-500 ml-1">*</span>
                </label>
                <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    maxLength={200}
                    autoComplete="email"
                    className={FIELD_CLASS}
                    placeholder={t("support_form_email_placeholder")}
                />
            </div>

            <div>
                <label htmlFor="message" className={LABEL_CLASS}>
                    {t("support_form_message")}
                    <span className="text-red-500 ml-1">*</span>
                </label>
                <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={7}
                    maxLength={4000}
                    className={`${FIELD_CLASS} resize-none`}
                    placeholder={t("support_form_message_placeholder")}
                />
            </div>

            {/* Une seule action à la fois : le bouton principal disparaît dès que
                le repli v2 s'affiche, sinon deux boutons identiques cohabitent et
                celui du haut rejoue la v3 qu'on vient d'échouer. */}
            {!showRecaptchaV2 && (
                <Button type="primary" block isLoading={loading} progress>
                    {loading
                        ? t("support_form_sending")
                        : t("support_form_submit")}
                </Button>
            )}

            {showRecaptchaV2 && (
                <div className="flex flex-col items-center gap-4">
                    <p className="text-sm text-center dark:text-lightBlue text-gray-500 mb-0">
                        {t("signup_recaptcha_verify")}
                    </p>
                    <ReCAPTCHA
                        ref={recaptchaV2Ref}
                        sitekey={
                            process.env.NEXT_PUBLIC_RECAPTCHA_V2_SITE_KEY || ""
                        }
                        onChange={setRecaptchaV2Token}
                    />
                    {recaptchaV2Token && (
                        <Button
                            type="primary"
                            block
                            isLoading={loading}
                            progress
                            onClick={handleRecaptchaV2Submit}
                        >
                            {loading
                                ? t("support_form_sending")
                                : t("support_form_submit")}
                        </Button>
                    )}
                </div>
            )}

            <p className="text-sm text-center dark:text-lightBlue text-gray-500 mb-0">
                {t("support_form_privacy")}
            </p>
        </form>
    );
}

export default function SupportForm() {
    return (
        <GoogleReCaptchaProvider
            reCaptchaKey={process.env.NEXT_PUBLIC_RECAPTCHA_V3_SITE_KEY}
            scriptProps={{
                async: true,
                defer: true,
            }}
        >
            <SupportFormInner />
        </GoogleReCaptchaProvider>
    );
}
